import { useState, useEffect } from "react";
import { auth, db } from "../firebase/firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./AdminLoginComponent.scss";

const AdminLoginComponent = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  const checkAdminAccess = async (uid) => {
    try {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        setErrorMessage("User profile not found.");
        return false;
      }

      const userData = userSnap.data();
      const userType = userData?.user_type;

      if (userType !== "admin") {
        setErrorMessage("Access denied. Admins only.");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Admin check error:", error);
      setErrorMessage("Failed to verify admin access.");
      return false;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const isAdmin = await checkAdminAccess(userCredential.user.uid);

      if (isAdmin) {
        navigate("/admindashboard");
      } else {
        setErrorMessage("Access denied. Admins only.");
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage("Login failed. Please check your email and password.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const validateCurrentUser = async () => {
      if (!user?.uid) return;

      const isAdmin = await checkAdminAccess(user.uid);

      if (isAdmin) {
        navigate("/admindashboard");
      }
    };

    validateCurrentUser();
  }, [user, navigate]);

  return (
    <div className="login">
      <div className="login__overlay">
        <div className="login__card">
          <div className="login__header">
            <h1 className="login__brand">Plan My Guest - Admin Login</h1>
          </div>

          <form className="login__form" onSubmit={handleLogin}>
            <div className="login__field">
              <label className="login__label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                className="login__input"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>

            <div className="login__field">
              <label className="login__label" htmlFor="password">
                Password
              </label>

              <div className="login__passwordWrap">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="login__input login__input--password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  className="login__togglePassword"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {errorMessage && <p className="login__error">{errorMessage}</p>}

            <ButtonComponent
              as="button"
              type="submit"
              className="login__submit ui-btn"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </ButtonComponent>
          </form>

          <div className="login__footer">
            <p className="login__signupText">
              Not registered yet?{" "}
              <Link to="/signup" className="login__signupLink">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginComponent;